import { useCallback, useEffect, useState } from 'react';
import { bankTransferProblem, formatIban, type BankTransferDetails } from '@lemontaigu/pms-core';
import { usePms } from './PmsProvider.js';

// « Virement » — the property's bank-transfer instructions for arrhes (PB10,
// PMS-PHONE-BOOKING-BRIEF §4.6). A SETTING, not text typed into each mail: the
// desk's confirmation renders it (account holder, IBAN, BIC, reference), so an
// account that changes is changed here once. Set under `pms.rate.write`, like the
// policies; the desk reads it (pms.reservation.read) to write the mail.
//
// The IBAN is checked as the table checks it (mod-97) before any round trip, so a
// mistyped digit is caught by the person typing it, not by the guest paying it.

type Fields = Omit<BankTransferDetails, 'propertyCode'>;

const EMPTY: Fields = { accountHolder: '', iban: '', bic: '', dueDays: 7 };

export function BankTransferSettings({ canEdit }: { canEdit: boolean }) {
  const pms = usePms();
  const [saved, setSaved] = useState<BankTransferDetails | null | undefined>(undefined);
  const [draft, setDraft] = useState<Fields | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await pms.bankTransfer.details();
    if (!r.ok) return setErr(r.error.message);
    setSaved(r.value);
  }, [pms]);

  useEffect(() => {
    void load();
  }, [load]);

  const problem = draft === null ? null : bankTransferProblem(draft);

  const save = async () => {
    if (draft === null || problem !== null) return;
    setBusy(true);
    setErr('');
    const r = await pms.bankTransfer.save(draft);
    setBusy(false);
    if (!r.ok) return setErr(r.error.message);
    setDraft(null);
    await load();
  };

  return (
    <section className="pms-form pms-bank" aria-labelledby="pms-bank-title">
      <h3 id="pms-bank-title">Virement — coordonnées bancaires des arrhes</h3>
      <p className="pms-sub">
        Reprises dans la confirmation envoyée au client quand les arrhes se règlent par virement. Le délai est compté depuis la réservation et ne
        passe jamais l’arrivée.
      </p>
      {err && <p className="auth-err">Erreur : {err}</p>}
      {saved === undefined && !err && <p className="pms-empty">Chargement…</p>}
      {saved === null && draft === null && (
        <p className="pms-hint">Aucune coordonnée pour cet établissement : le virement n’est pas proposé à la réception.</p>
      )}
      {saved && draft === null && (
        <dl>
          <dt>Bénéficiaire</dt>
          <dd>{saved.accountHolder}</dd>
          <dt>IBAN</dt>
          <dd>{formatIban(saved.iban)}</dd>
          <dt>BIC / SWIFT</dt>
          <dd>{saved.bic}</dd>
          <dt>Délai de virement</dt>
          <dd>{saved.dueDays} jours</dd>
        </dl>
      )}
      {canEdit && draft === null && saved !== undefined && (
        <div className="pms-form-actions">
          <button
            type="button"
            className="pms-mini"
            onClick={() =>
              setDraft(saved ? { accountHolder: saved.accountHolder, iban: formatIban(saved.iban), bic: saved.bic, dueDays: saved.dueDays } : { ...EMPTY })
            }
          >
            {saved ? 'Modifier' : 'Saisir les coordonnées'}
          </button>
        </div>
      )}
      {draft !== null && (
        <>
          <label className="pms-field">
            <span>Bénéficiaire</span>
            <input value={draft.accountHolder} onChange={(e) => setDraft({ ...draft, accountHolder: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>IBAN</span>
            <input value={draft.iban} autoComplete="off" spellCheck={false} onChange={(e) => setDraft({ ...draft, iban: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>BIC / SWIFT</span>
            <input value={draft.bic} autoComplete="off" spellCheck={false} onChange={(e) => setDraft({ ...draft, bic: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>Délai de virement (jours)</span>
            <input
              inputMode="numeric"
              value={String(draft.dueDays)}
              onChange={(e) => setDraft({ ...draft, dueDays: Number(e.target.value.replace(/\D/g, '') || 0) })}
            />
          </label>
          {problem && (
            <p className="pms-hint" role="alert">
              {problem}
            </p>
          )}
          <div className="pms-form-actions">
            <button type="button" className="auth-btn" disabled={busy || problem !== null} onClick={() => void save()}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              className="auth-btn is-ghost"
              onClick={() => {
                setDraft(null);
                setErr('');
              }}
            >
              Annuler
            </button>
          </div>
        </>
      )}
    </section>
  );
}
